import { StaticDocument } from '@inyova/models';
import { orderBy } from 'lodash-es';

import { Action, createReducer, createSelector, on } from '@ngrx/store';
import * as ProfileActions from './profile.actions';
import { selectProfileFeature } from 'src/app/app.reducers';

import { Documents3APagination } from '@app/app.constants';
import { GrowDocument, ProfileState } from '@shared/models/Profile';

export const PROFILE_REDUCER_FEATURE_KEY = 'profile';

export const initialState: ProfileState = {
  isInitialised: false,
  personalDetails: {
    email: '',
    first_name: '',
    last_name: '',
    language: '',
    street: '',
    number: '',
    zip: '',
    country: '',
    city: '',
    phone: '',
    verified_phone: '',
    phone_verified: false,
    unverified_phone: ''
  },
  countries: [],
  personalDetailsCurrentScreen: 'main',
  personalDetailsLoading: false,
  show2FADisableModal: false,
  twoFA: {
    loading: false,
    currentScreen: 'main'
  },
  status: '',
  chCities: [],
  chCitiesLoaded: false,
  validatePhone: false,
  validatePhoneError: false,
  passwordValidated: false,
  showI4KNameList: true,
  loading: false,
  customerDocuments: {
    initialized: false,
    docs: []
  },
  documents3A: {
    initialized: false,
    docs: [],
    pageIndex: 1,
    filter: 'all',
    sort: '',
    pages: 0,
    total: 0
  },
  documentsGrow: {
    initialized: false,
    docs: []
  },
  staticDocuments: {
    initialized: false,
    docs: []
  }
};

const profileReducer = createReducer(
  initialState,
  on(
    ProfileActions.getPersonalDetailsSuccess,
    (state, action): ProfileState => ({
      ...state,
      personalDetails: action.personalDetails,
      isInitialised: true
    })
  ),
  on(
    ProfileActions.updatePersonalDetailsCurrentScreen,
    (state, action): ProfileState => ({
      ...state,
      personalDetailsCurrentScreen: action.screen
    })
  ),
  on(
    ProfileActions.fetchCountriesSuccess,
    (state, action): ProfileState => ({
      ...state,
      countries: action.countries
    })
  ),
  on(
    ProfileActions.update2FACurrentScreen,
    (state, action): ProfileState => ({
      ...state,
      twoFA: {
        ...state.twoFA,
        currentScreen: action.screen
      }
    })
  ),
  on(
    ProfileActions.updateShow2FADisableModal,
    (state, action): ProfileState => ({
      ...state,
      show2FADisableModal: action.show
    })
  ),
  on(
    ProfileActions.update2FALoading,
    (state, action): ProfileState => ({
      ...state,
      twoFA: {
        ...state.twoFA,
        loading: action.loading
      }
    })
  ),
  on(
    ProfileActions.updatePersonalDetailsLoading,
    (state, action): ProfileState => ({
      ...state,
      personalDetailsLoading: action.loading
    })
  ),
  on(
    ProfileActions.validatePassword2FA,
    ProfileActions.send2FAAuthorizationCode,
    ProfileActions.updateCustomerPhoneAndSend2FACode,
    ProfileActions.toggle2FAAuthorization,
    (state): ProfileState => ({
      ...state,
      twoFA: {
        ...state.twoFA,
        loading: true
      }
    })
  ),
  on(ProfileActions.logout, () => ({ ...initialState })),
  on(
    ProfileActions.updateProfilePasswordStatus,
    (state, action): ProfileState => ({
      ...state,
      status: action.status
    })
  ),
  on(ProfileActions.fetchCustomerDocsSuccess, (state, action): ProfileState => {
    return {
      ...state,
      customerDocuments: {
        initialized: true,
        docs: action.documents
      }
    };
  }),
  on(ProfileActions.fetchCustomerDocsFail, (state): ProfileState => {
    return {
      ...state,
      customerDocuments: {
        initialized: true,
        docs: []
      }
    };
  }),
  on(ProfileActions.fetchLibertyDocsSuccess, (state, action): ProfileState => {
    return {
      ...state,
      documents3A: {
        ...state.documents3A,
        initialized: true,
        ...action
      }
    };
  }),
  on(ProfileActions.fetchGrowDocsSuccess, (state, action): ProfileState => {
    return {
      ...state,
      documentsGrow: {
        ...state.documentsGrow,
        initialized: true,
        docs: action.documents
      }
    };
  }),
  on(ProfileActions.fetchLibertyDocsFail, (state): ProfileState => {
    return {
      ...state,
      documents3A: {
        ...state.documents3A,
        initialized: true,
        docs: []
      }
    };
  }),
  on(
    ProfileActions.getChCity,
    (state): ProfileState => ({
      ...state,
      chCitiesLoaded: false
    })
  ),
  on(
    ProfileActions.getChCitySuccess,
    (state, action): ProfileState => ({
      ...state,
      chCities: action.response.records.map((item) => item.fields.ortbez18).sort(),
      chCitiesLoaded: true
    })
  ),
  on(
    ProfileActions.getChCityFailed,
    (state): ProfileState => ({
      ...state,
      chCitiesLoaded: false
    })
  ),
  on(
    ProfileActions.resetChCity,
    (state): ProfileState => ({
      ...state,
      chCities: [],
      chCitiesLoaded: false
    })
  ),
  on(
    ProfileActions.updateProfileAddress,
    (state): ProfileState => ({
      ...state,
      loading: true
    })
  ),
  on(
    ProfileActions.updateProfileAddressSuccess,
    (state, action): ProfileState => ({
      ...state,
      personalDetails: {
        ...state.personalDetails,
        street: action.personalDetails.street,
        number: action.personalDetails.number,
        zip: action.personalDetails.zip,
        city: action.personalDetails.city
      },
      loading: false,
      personalDetailsCurrentScreen: 'main' as const
    })
  ),
  on(
    ProfileActions.updateProfileAddressFailed,
    (state): ProfileState => ({
      ...state,
      loading: false
    })
  ),
  on(
    ProfileActions.updateProfileEmail,
    (state): ProfileState => ({
      ...state,
      loading: true
    })
  ),
  on(
    ProfileActions.updateProfileEmailSuccess,
    (state, action): ProfileState => ({
      ...state,
      personalDetails: {
        ...state.personalDetails,
        email: action.personalDetails.email
      },
      loading: false,
      personalDetailsCurrentScreen: 'main' as const
    })
  ),
  on(
    ProfileActions.updateProfileEmailFailed,
    (state): ProfileState => ({
      ...state,
      loading: false
    })
  ),
  on(
    ProfileActions.updateProfileMobilePhone,
    (state): ProfileState => ({
      ...state,
      loading: true
    })
  ),
  on(
    ProfileActions.updateProfileMobilePhoneSuccess,
    (state, action): ProfileState => ({
      ...state,
      personalDetails: {
        ...state.personalDetails,
        phone: action.personalDetails.phone
      },
      loading: false
    })
  ),
  on(
    ProfileActions.updateProfileMobilePhoneFailed,
    (state): ProfileState => ({
      ...state,
      loading: false
    })
  ),
  on(
    ProfileActions.updateProfileOwnerName,
    (state): ProfileState => ({
      ...state,
      loading: true
    })
  ),
  on(
    ProfileActions.updateProfileOwnerSuccess,
    (state): ProfileState => ({
      ...state,
      showI4KNameList: true,
      loading: false
    })
  ),
  on(
    ProfileActions.updateProfileOwnerFailed,
    (state): ProfileState => ({
      ...state,
      loading: false
    })
  ),
  on(
    ProfileActions.toggleProfileI4KList,
    (state, action): ProfileState => ({
      ...state,
      showI4KNameList: action.showI4KNameList
    })
  ),
  on(
    ProfileActions.sendOtpCode,
    (state): ProfileState => ({
      ...state,
      loading: true
    })
  ),
  on(
    ProfileActions.sendOtpCodeSuccess,
    (state): ProfileState => ({
      ...state,
      validatePhone: true,
      loading: false
    })
  ),
  on(
    ProfileActions.sendOtpCodeFailed,
    (state): ProfileState => ({
      ...state,
      loading: false,
      personalDetailsCurrentScreen: 'main' as const,
      validatePhone: false
    })
  ),
  on(
    ProfileActions.sendOtpCodeReset,
    (state): ProfileState => ({
      ...state,
      validatePhone: false,
      validatePhoneError: false
    })
  ),
  on(
    ProfileActions.validateMobilePhone,
    (state): ProfileState => ({
      ...state,
      validatePhoneError: false,
      loading: true
    })
  ),
  on(
    ProfileActions.validateMobilePhoneSuccess,
    (state): ProfileState => ({
      ...state,
      loading: false,
      personalDetailsCurrentScreen: 'main' as const,
      validatePhone: false
    })
  ),
  on(
    ProfileActions.validateMobilePhoneFailed,
    (state): ProfileState => ({
      ...state,
      loading: false,
      validatePhoneError: true
    })
  ),
  on(
    ProfileActions.validatePassword,
    (state): ProfileState => ({
      ...state,
      loading: true
    })
  ),
  on(
    ProfileActions.validatePasswordSuccess,
    (state): ProfileState => ({
      ...state,
      loading: false,
      passwordValidated: true
    })
  ),
  on(
    ProfileActions.validatePasswordFailed,
    (state): ProfileState => ({
      ...state,
      loading: false,
      passwordValidated: false
    })
  ),
  on(
    ProfileActions.validatePasswordReset,
    (state): ProfileState => ({
      ...state,
      passwordValidated: false
    })
  ),
  on(
    ProfileActions.fetchStaticDocsSuccess,
    (state, action): ProfileState => ({
      ...state,
      staticDocuments: { initialized: true, docs: action.documents }
    })
  ),
  on(
    ProfileActions.fetchStaticDocsFail,
    (state): ProfileState => ({
      ...state,
      staticDocuments: { initialized: true, docs: [] }
    })
  )
);

export function reducer(state: ProfileState | undefined, action: Action): ProfileState {
  return profileReducer(state, action);
}

/*
 * SELECTORS
 * */

export const selectIsInitialised = createSelector(selectProfileFeature, (state: ProfileState) => state.isInitialised);
// Strategy reducer selectors
export const selectPersonalDetails = createSelector(selectProfileFeature, (state: ProfileState) => state.personalDetails);
export const selectPersonalDetailsCurrentScreen = createSelector(selectProfileFeature, (state: ProfileState) => state.personalDetailsCurrentScreen);
export const selectCountries = createSelector(selectProfileFeature, (state: ProfileState) => state.countries);
export const selectShow2FADisableModal = createSelector(selectProfileFeature, (state: ProfileState) => state.show2FADisableModal);
export const selectPersonalDetailsLoading = createSelector(selectProfileFeature, (state: ProfileState) => state.personalDetailsLoading);

export const selectTransfer3ADoc = createSelector(selectProfileFeature, (state: ProfileState) => {
  if (!state || !state.customerDocuments || !state.customerDocuments.initialized) return [];
  return state.customerDocuments.docs.filter((doc) => doc.attributes.type_id === 24);
});

export const select2FA = createSelector(selectProfileFeature, (state: ProfileState) => state.twoFA);
export const selectProfilePasswordStatus = createSelector(selectProfileFeature, (state: ProfileState) => state.status);

export const selectGeneralDocuments = createSelector(selectProfileFeature, (state: ProfileState) => {
  const { initialized } = state.customerDocuments;

  const accountSpecificDocIDs = [5, 22, 23, 24, 42, 43];
  const docs = state.customerDocuments.docs
    .filter((doc) => !accountSpecificDocIDs.includes(doc.attributes.type_id))
    .sort((a, b) => b.attributes.type_id - a.attributes.type_id);

  return { initialized, docs };
});

export const select3BDocuments = createSelector(selectProfileFeature, (state: ProfileState) => {
  const { initialized } = state.customerDocuments;
  const reportingDocs = state.customerDocuments.docs.filter((doc) => doc.attributes.type_id === 22);
  const taxDocs = state.customerDocuments.docs.filter((doc) => doc.attributes.type_id === 5);

  return { initialized, taxDocs, reportingDocs };
});

export const selectCIDocuments = createSelector(selectProfileFeature, (state: ProfileState) => {
  const { initialized } = state.customerDocuments;
  const docs = state.customerDocuments.docs
    .filter((doc) => doc.attributes.type_id === 42 || doc.attributes.type_id === 43)
    .sort((a, b) => a.attributes.type_id - b.attributes.type_id);

  return { initialized, docs };
});

export const select3ADocuments = createSelector(selectProfileFeature, (state: ProfileState) => {
  return state.documents3A;
});
export const selectGrowDocuments = createSelector(selectProfileFeature, (state: ProfileState) => {
  const { initialized, docs } = state.documentsGrow;

  const groupedDocuments: Record<string, { title: string; documents: GrowDocument[] }> = {};

  for (const document of docs) {
    if (!groupedDocuments[document.project_id]) {
      groupedDocuments[document.project_id] = { title: document.project_title, documents: [] };
    }
    groupedDocuments[document.project_id].documents.push(document);
  }

  const projects = Object.values(groupedDocuments);
  return { initialized, projects };
});

export const selectChCities = createSelector(selectProfileFeature, (state: ProfileState) => state.chCities);
export const selectChCitiesLoaded = createSelector(selectProfileFeature, (state: ProfileState) => state.chCitiesLoaded);
export const selectValidatePhone = createSelector(selectProfileFeature, (state: ProfileState) => state.validatePhone);
export const selectValidatePhoneError = createSelector(selectProfileFeature, (state: ProfileState) => state.validatePhoneError);
export const selectPasswordValidated = createSelector(selectProfileFeature, (state: ProfileState) => state.passwordValidated);
export const selectShowI4KNameList = createSelector(selectProfileFeature, (state: ProfileState) => state.showI4KNameList);
export const selectLoading = createSelector(selectProfileFeature, (state: ProfileState) => state.loading);

export const selectStaticDocuments = createSelector(selectProfileFeature, (state: ProfileState) => {
  const { initialized, docs } = state.staticDocuments;
  let agmDocs: StaticDocument[] = [];
  let egmDocs: StaticDocument[] = [];
  let static3aDocs: StaticDocument[] = [];
  let staticGeneralDocs: StaticDocument[] = [];
  let CIUpdates: StaticDocument[] = [];

  for (const doc of docs) {
    switch (doc.attributes.account_type) {
      case 'crowd_investing':
        switch (doc.attributes.category) {
          case 'agm':
            agmDocs = [...agmDocs, doc];
            break;
          case 'egm':
            egmDocs = [...egmDocs, doc];
            break;
          case 'updates':
            CIUpdates = [...CIUpdates, doc];
            break;
          default:
            break;
        }
        break;
      case '3a':
        static3aDocs = [...static3aDocs, doc];
        break;
      case '3b':
      case 'grow':
        staticGeneralDocs = [...staticGeneralDocs, doc];
        break;
    }
  }

  return { initialized, CIUpdates, agmDocs, egmDocs, static3aDocs, staticGeneralDocs };
});

export const select3ADocumentsPaged = createSelector(select3ADocuments, selectStaticDocuments, (docs3A, docsStatic) => {
  if (!docs3A.initialized && !docsStatic.initialized) {
    return {
      initialized: false,
      docs: [],
      pageIndex: 1,
      pages: 0,
      filter: 'all',
      sort: '',
      total: 0
    };
  }
  // convert static docs as a liberty data modal
  const static3a = docsStatic.static3aDocs.map((item) => {
    return {
      type: 'liberty_static',
      id: item.id,
      attributes: {
        document_type: item.attributes.title,
        document_type_code: 'static',
        document_date: item.attributes.date,
        ...(typeof item.attributes.externalUrl !== 'undefined' && { document_external_url: item.attributes.externalUrl })
      }
    };
  });

  const { docs, initialized, pageIndex, filter, total, sort } = docs3A;

  const sortArray = sort.split(',');
  let titleSort: false | 'asc' | 'desc' = false;
  let dateSort: false | 'asc' | 'desc' = false;

  if (sortArray.includes('document_date')) {
    dateSort = 'asc';
  }

  if (sortArray.includes('-document_date')) {
    dateSort = 'desc';
  }
  if (sortArray.includes('document_type')) {
    titleSort = 'asc';
  }

  if (sortArray.includes('-document_type')) {
    titleSort = 'desc';
  }

  let finalDocs = docs;

  if ((filter === 'all' && pageIndex === 1) || (filter === 'static' && pageIndex === 1)) {
    finalDocs = [...static3a, ...docs];
    if (titleSort || dateSort) {
      if (titleSort && dateSort) {
        finalDocs = orderBy(finalDocs, [(item) => item.attributes.document_type, (item) => item.attributes.document_date], [titleSort, dateSort]);
      } else if (titleSort) {
        finalDocs = orderBy(finalDocs, [(item) => item.attributes.document_type], [titleSort]);
      } else if (dateSort) {
        finalDocs = orderBy(finalDocs, [(item) => item.attributes.document_date], [dateSort]);
      }
    }
  }

  const numberOfPages = Math.ceil(total / Documents3APagination.DEFAULT_RANGE);

  return {
    initialized,
    docs: finalDocs,
    pageIndex,
    pages: numberOfPages,
    filter,
    total,
    sort
  };
});

export const selectAllDocuments = (includeGeneral: boolean, include3A: boolean, includeGrow: boolean) =>
  createSelector(
    selectGeneralDocuments,
    select3BDocuments,
    select3ADocumentsPaged,
    selectGrowDocuments,
    selectStaticDocuments,
    selectCIDocuments,
    (docsGeneral, docs3b, docs3a, docsGrow, docsStatic, docsCI) => {
      const initGeneral = includeGeneral ? docsGeneral.initialized : true;
      const init3A = include3A ? docs3a.initialized : true;
      const initGrow = includeGrow ? docsGrow.initialized : true;
      const initialized = initGeneral && init3A && initGrow && docsStatic.initialized;

      return {
        initialized,
        docsGeneral,
        docs3b,
        docs3a,
        docsGrow,
        docsStatic,
        docsCI
      };
    }
  );

export const selectStaticDocumentsByAccType = (filter: 'crowd_investing' | '3a' | '3b' | 'grow') =>
  createSelector(selectProfileFeature, (state: ProfileState) => {
    const docs = state.staticDocuments.docs.filter((doc) => doc.attributes.account_type === filter);
    return { docs, initialized: state.staticDocuments.initialized };
  });

export const selectGeneralStaticDocuments = () =>
  createSelector(selectProfileFeature, (state: ProfileState) => {
    const docs = state.staticDocuments.docs.filter((doc) => doc.attributes.account_type === '3b' || doc.attributes.account_type === 'grow');
    return { docs, initialized: state.staticDocuments.initialized };
  });

export const selectPolyregDocument = createSelector(selectStaticDocuments, (docsStatic) => {
  return docsStatic.staticGeneralDocs.filter((doc) => doc.attributes.category === 'polyreg')[0];
});
